import { hasYesOrNo } from 'models/validate';
import locationStateModelValidator from 'forms/shared/validators/models/location/models/state';
import {
  militaryServiceOptions,
  militaryServiceOptionsPvq,
  militaryServiceOptionsSF852013,
} from 'constants/enums/militaryServiceOptions';
import { militaryStatusOptions, militaryStatusOptionsPvq } from 'constants/enums/militaryStatusOptions';
import militaryOfficerOptions from 'constants/enums/militaryOfficerOptions';
import { militaryDischargeOptions, militaryDischargeOptionsPvq } from 'constants/enums/militaryDischargeOptions';
import { DEFAULT_LATEST } from 'constants/dateLimits';
import { DateModel, DateRangeModel, ValidateJSModel } from 'forms/shared/types';
import { setToStartOfMonth, getSafeDateTime, isEndBeforeStart } from 'helpers/date';
import { servicePattern } from 'constants/patterns';
import { SF852013, SF85 } from 'constants/formTypes';
import { isPVQForm } from 'helpers/form';
import pvqMilitaryDisciplineItemValidator from 'forms/sections/military/disciplinary/validators/pvq-item';
import { DeepSafePartial } from 'types/types';
import { MilitaryHistoryItemModel } from '../types';
import { FormDetails } from '../../../../../util/singletons';

type Attributes = DeepSafePartial<MilitaryHistoryItemModel>;

const isSF852013 = () => {
  const formDetails = FormDetails.getInstance().getData();
  return (
    (formDetails.formType === SF85 || formDetails.formType === SF852013)
    && formDetails.formVersion === '2013-12'
  );
};

const isPvq = (): boolean => isPVQForm(FormDetails.getInstance().getData().formType);

const isAirOrArmyNationalGuard = (serviceType?: Partial<MilitaryHistoryItemModel['Service']>) => serviceType
  && serviceType.value
  && !isSF852013()
  && ['AirNationalGuard', 'ArmyNationalGuard'].includes(serviceType.value);

// this is used on the SF85 2013
const isNationalGuard = (serviceType?: Partial<MilitaryHistoryItemModel['Service']>) => serviceType && serviceType.value && isSF852013() && ['NationalGuard'].includes(serviceType.value);

// Only used if SF852013 && National Guard
const requiresCountryNationalGuard = (attributes: DeepSafePartial<MilitaryHistoryItemModel>) => (
  isNationalGuard(attributes?.Service) && !attributes?.ServiceState?.state);

// Only used if SF852013 && National Guard

const requiresState = (attributes: DeepSafePartial<MilitaryHistoryItemModel>) => isNationalGuard(attributes?.Service) && attributes?.Country?.value?.includes('United States');

const militaryHistoryItemValidator: ValidateJSModel<MilitaryHistoryItemModel> = {

  List: () => (isPvq() ? {
    branchCollection: {
      validator: pvqMilitaryDisciplineItemValidator,
    },
    presence: true,
  } : {}),

  Rank: () => (isPvq() ? { hasValue: true } : {}),

  Service: () => {
    let inclusion: string[];

    if (isSF852013()) {
      inclusion = militaryServiceOptionsSF852013;
    } else if (isPvq()) {
      inclusion = militaryServiceOptionsPvq;
    } else {
      inclusion = militaryServiceOptions;
    }

    return {
      hasValue: {
        validator: {
          inclusion,
        }
      }
    };
  },

  Status: () => ({
    hasValue: {
      validator: {
        inclusion: isPvq() ? militaryStatusOptionsPvq : militaryStatusOptions,
      },
    }
  }),

  StatusOtherExplanation: (value, attributes: Attributes) => (isPvq() && attributes?.Status?.value === 'Other' ? { hasValue: true } : {}),

  Officer: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return {
      hasValue: {
        validator: {
          inclusion: militaryOfficerOptions,
        },
      },
    };
  },

  Dates: (value: DateRangeModel, attributes, attributeName, options) => {
    const additional = isEndBeforeStart(getSafeDateTime(value?.to), getSafeDateTime(value?.from))
      ? { tooEarly: 'error.daterange.order', requireYear: false }
      : {};
    return {
      presence: true,
      daterange: {
        earliest: setToStartOfMonth(options?.applicantBirthdate) as DateModel,
        latest: DEFAULT_LATEST,
        ...additional,
      },
    };
  },

  ServiceNumber: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return {
      hasValue: {
        validator: {
          format: {
            pattern: servicePattern,
            message: 'error.military.serviceNumber.pattern',
          },
        },
      },
    };
  },

  ServiceState: (value, attributes: Attributes) => {
    if (isAirOrArmyNationalGuard(attributes?.Service)) {
      return {
        presence: true,
        location: { validator: locationStateModelValidator },
      };
    }

    if (requiresState(attributes)) { // only occurs on SF852013
      return {
        presence: true,
        location: { validator: locationStateModelValidator },
      };
    }

    return {};
  },

  HasBeenDischarged: (value, attributes, attributeName, options) => {
    if (options?.requireMilitaryHasBeenDischarged === false) { return {}; }

    return {
      hasValue: { validator: hasYesOrNo },
    };
  },

  DischargeType: (value, attributes: Attributes, attributeName, options) => {
    if (attributes?.HasBeenDischarged?.value === 'Yes'
      || (isPVQForm(options?.formType) && !attributes?.Dates?.present)) { // eslint-disable-line @typescript-eslint/no-unsafe-member-access
      return {
        hasValue: {
          validator: {
            inclusion: isPvq() ? militaryDischargeOptionsPvq : militaryDischargeOptions,
          },
        },
      };
    }

    return {};
  },

  DischargeReason: (value, attributes: Attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && !attributes?.Dates?.present
    && attributes?.DischargeType?.value !== 'Honorable') { // eslint-disable-line @typescript-eslint/no-unsafe-member-access
      return { hasValue: true };
    }
    if (attributes?.HasBeenDischarged?.value === 'No'
      || attributes?.DischargeType?.value === 'Honorable'
      || !options?.requireMilitaryHasBeenDischarged) {
      return {};
    }

    return { hasValue: true };
  },

  DischargeTypeOther: (value, attributes: Attributes) => {
    if (attributes?.DischargeType?.value === 'Other') { return { hasValue: true }; }

    return {};
  },

  DischargeDate: (value, attributes: Attributes, attributeName, options) => {
    if (attributes?.HasBeenDischarged?.value === 'Yes') {
      return {
        presence: true,
        date: { earliest: setToStartOfMonth(options?.applicantBirthdate) as DateModel, latest: DEFAULT_LATEST },
      };
    }

    return {};
  },

  Country: (value, attributes: Attributes, attributeName, options) => {
    if (!attributes?.CountryFocused && (options?.requireMilitaryCountry === true
      || (isNationalGuard(attributes?.Service)
        && requiresCountryNationalGuard(attributes)))) {
      if (options?.formType === SF852013) {
        return {
          presence: true,
          country: { excludeUnitedStates: false },
          hasValue: true,
        };
      }
      return {
        presence: true,
        country: { excludeUnitedStates: true },
      };
    }

    return {};
  },

  Comments: {},
  DischargeComments: {},
  OtherThanHonorableComments: {},
};

export default militaryHistoryItemValidator;
